/**
 * ScrollProgress — Accent-colored reading progress bar.
 *
 * A 2px bar fixed to the top of the viewport that fills left-to-right
 * as the user scrolls through the page. Uses requestAnimationFrame for
 * smooth 60fps updates with zero layout thrash.
 *
 * Reads scroll position from the nearest scrollable ancestor (the <main>
 * element in AppLayout) rather than document.documentElement.
 */
import { useEffect, useRef, useState } from 'react';

export default function ScrollProgress() {
  const barRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Find the scrollable <main> element (lex-page-content class)
    const scrollHost =
      document.querySelector('main.lex-page-content') as HTMLElement | null;
    if (!scrollHost) return;

    let raf = 0;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollHost;
      const scrollable = scrollHeight - clientHeight;

      // Only show when page is actually scrollable (>50px overflow)
      if (scrollable <= 50) {
        setVisible(false);
        return;
      }

      setVisible(true);
      const pct = Math.min(scrollTop / scrollable, 1);
      if (barRef.current) {
        barRef.current.style.transform = `scaleX(${pct})`;
      }
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    // Initial check
    update();

    scrollHost.addEventListener('scroll', onScroll, { passive: true });
    // Re-check on resize (content may become scrollable/unscrollable)
    window.addEventListener('resize', update, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      scrollHost.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', update);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      ref={barRef}
      className="fixed top-0 left-0 right-0 z-50 h-[2px] origin-left pointer-events-none"
      style={{
        background: 'var(--lex-accent-primary)',
        transform: 'scaleX(0)',
        willChange: 'transform',
      }}
    />
  );
}
